import store from '@/store';

/**
 * Route guard for kindergarten module availability.
 *
 * The API is the real gate (`kg.module` middleware) — without this the page mounts,
 * its first request 403s with `module_blocked`, and the interceptor redirects to
 * /access-restricted. That works, but only once a request actually fires: a view
 * whose fetch is conditional (e.g. Plan.vue needs a selected course) renders empty
 * chrome forever instead. This guard makes the redirect immediate and unconditional.
 *
 * Only use it where the active kindergarten is the context. Views that resolve a
 * different kindergarten themselves (poll/questionnaire fill links) must keep
 * relying on the API, which checks the module against the right kindergarten.
 *
 * @param {string} module      key from KindergartenModule::MODULES
 * @param {Function} [guard]   existing beforeEnter to run once the module check passes
 */
export const requireModule = (module, guard = null) => {
    const enter = (to, from, next) => {
        const blocked = store.getters.moduleBlockInfo(module);

        if (blocked) {
            // Carry the same reason the API reports, so Error403 can explain the
            // block (unpaid invoices, premium) instead of falling back to the
            // generic permission message. A feature blocked by its parent reports
            // the parent's reason.
            next(accessRestrictedLocation(blocked.reason, blocked.block_reason));
            return;
        }

        if (guard) {
            guard(to, from, next);
            return;
        }

        next();
    };

    // Stamped on the guard so moduleForRoute() can recover it. vue-router keeps
    // beforeEnter on the route record, and `meta` is already used as a plain
    // string by the survey routes, so it is not available for this.
    enter.module = module;

    return enter;
};

/**
 * Labels a route with its module WITHOUT checking it on entry.
 *
 * For views that switch to a different kindergarten after the guard has run — the
 * child profile tabs load the child's own kindergarten in the page itself. Checking
 * on entry would test whichever kindergarten was active at boot and wrongly redirect.
 * The label still lets the http interceptor recognise a mid-session module_blocked
 * for this page and redirect, and the API checks the child's kindergarten.
 */
export const markModule = (module, guard = null) => {
    const enter = (to, from, next) => {
        if (guard) {
            guard(to, from, next);
            return;
        }

        next();
    };

    enter.module = module;

    return enter;
};

/**
 * Where a module block sends the user. Error403 reads only `reason` and
 * `block_reason`, and block_reason is left out when there is none, so the URL
 * carries nothing the page does not use.
 */
export const accessRestrictedLocation = (reason, block_reason = null) => {
    const query = {reason};

    if (block_reason) {
        query.block_reason = block_reason;
    }

    return {path: '/access-restricted', query};
};

/**
 * The module a route belongs to, or null if it is not module-gated.
 * Walks the matched records so a child inherits its parent's module.
 */
export const moduleForRoute = (route) => {
    if (!route || !route.matched) {
        return null;
    }

    return route.matched
        .map(record => record.beforeEnter && record.beforeEnter.module)
        .filter(Boolean)
        .pop() || null;
};
